import { X, ArrowRight, Shield, CheckCircle2, XCircle, Eye, Bot, Zap, BarChart3, BookOpen } from "lucide-react";
import { Link } from "react-router";
import { useEffect, useRef } from "react";

interface HowItWorksModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Standard focusable selector for the focus trap (WAI-ARIA APG).
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function HowItWorksModal({ isOpen, onClose }: HowItWorksModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  // Capture invoker on open so ESC / close can return focus per WAI-ARIA
  // dialog pattern. Without it keyboard users get dumped to <body>.
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      const prev = previousFocusRef.current;
      if (prev && document.contains(prev)) prev.focus();
      return;
    }
    previousFocusRef.current = (document.activeElement as HTMLElement) ?? null;
    closeButtonRef.current?.focus();

    // ESC + focus trap (real implementation matches KeyboardShortcutsModal).
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusables.length === 0) { e.preventDefault(); return; }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    // Body scroll-lock — prevents page-scroll-leak while the modal is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="how-it-works-title"
        className="adp-modal relative w-[90vw] max-w-[1000px] max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-start justify-between pb-4 border-b border-border flex-shrink-0">
          <div className="flex items-start gap-3 flex-1">
            <div className="p-2 border bg-surface-1 border-border">
              <BookOpen className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h2 id="how-it-works-title" className="text-lg font-semibold text-foreground uppercase tracking-wide">
                How ADJUDON Works
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                AI Agent Governance Architecture
              </p>
            </div>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-surface-2 border border-transparent hover:border-border transition-colors flex-shrink-0 focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-8 pt-6 flex-1 overflow-y-auto">
          {/* Status disclosure — mirrors the Trust Center methodology page
              (landing-page/.../trust-center/methodology/audit-trail). This
              modal describes the full architecture; some witness layers
              are gated on commercial founder actions and run as
              deterministic mocks until each gate fires. Anti-§5-UWG: the
              body sections below use architectural present-tense ("signed
              by D-Trust QTSP", "chained to D-Trust QTSP") which is
              accurate to the design but not the per-tenant live status. */}
          <div className="border border-border bg-surface-1 px-4 py-3 text-xs text-muted-foreground">
            <span className="text-foreground font-semibold">Status today:</span>{" "}
            The SHA-256 hash chain, RFC 8785 canonicalization, daily Merkle
            anchor procedure, and 3-role approval gating run on every tenant.
            Witness layers below — D-Trust QTSP eIDAS RFC 3161 timestamps,
            OpenTimestamps Bitcoin anchoring, Trillian Tessera witness log —
            are architecturally wired and run as deterministic mocks that
            preserve chain integrity; per-layer live activation is gated on
            commercial founder actions (QTSP contract, OTS endpoints,
            Tessera deployment). See{" "}
            <a
              href="https://adjudon.com/trust-center/methodology/audit-trail"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              Trust Center methodology
            </a>{" "}
            for the full per-layer status.
          </div>

          {/* Architecture Diagram */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wider">
              System Architecture
            </h3>
            
            {/* Visual Flow */}
            <div className="border border-border p-6 bg-surface-1">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-center">
                {/* 1. AI Agent */}
                <div className="text-center">
                  <div className="w-full aspect-square bg-surface-1 border border-border flex items-center justify-center mb-3">
                    <div className="text-center">
                      <Bot className="w-8 h-8 text-muted-foreground mx-auto mb-1" />
                      <div className="text-xs font-semibold text-foreground">AI Agent</div>
                      <div className="text-[10px] text-muted-foreground mt-1">Your System</div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Wants to execute action
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex justify-center">
                  <ArrowRight className="w-8 h-8 text-muted-foreground" />
                </div>

                {/* 2. ADJUDON Proxy */}
                <div className="text-center">
                  <div className="w-full aspect-square bg-surface-1 border border-border flex items-center justify-center mb-3">
                    <div className="text-center">
                      <Shield className="w-10 h-10 text-muted-foreground mx-auto mb-1" />
                      <div className="text-xs font-semibold text-foreground">ADJUDON</div>
                      <div className="text-[10px] text-muted-foreground mt-1">Proxy Layer</div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Evaluates decision
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex justify-center">
                  <ArrowRight className="w-8 h-8 text-muted-foreground" />
                </div>

                {/* 3. Decision Point */}
                <div className="text-center">
                  <div className="w-full aspect-square bg-surface-1 border border-border flex items-center justify-center mb-3">
                    <div className="text-center p-3">
                      <div className="text-xs font-semibold text-foreground mb-2">Decision</div>
                      <div className="space-y-1 text-[10px]">
                        <div className="flex items-center gap-1.5 text-confidence">
                          <CheckCircle2 className="w-3 h-3" />
                          <span>Approve</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-review">
                          <Eye className="w-3 h-3" />
                          <span>Review</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-enforcement">
                          <XCircle className="w-3 h-3" />
                          <span>Block</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Based on policies
                  </div>
                </div>
              </div>

              {/* Explanation */}
              <div className="mt-6 pt-6 border-t border-border">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-xs">
                  <div className="p-3 bg-surface-2 border border-border">
                    <div className="font-semibold text-foreground mb-1">1. Intercept</div>
                    <div className="text-muted-foreground">
                      Adjudon sits between your AI agent and its actions, intercepting every decision before execution. Fail-open: SDK pass-through if Adjudon is unreachable.
                    </div>
                  </div>
                  <div className="p-3 bg-surface-2 border border-border">
                    <div className="font-semibold text-foreground mb-1">2. Evaluate</div>
                    <div className="text-muted-foreground">
                      Confidence scoring (3-pillar triangulation) + Policy Engine (ADL → Cedar substrate) + PII scrub + anomaly checks — p95 under 25 ms.
                    </div>
                  </div>
                  <div className="p-3 bg-surface-2 border border-border">
                    <div className="font-semibold text-foreground mb-1">3. Enforce + record</div>
                    <div className="text-muted-foreground">
                      Action ladder: block always wins; flag + auto_approve → auto-approve (LD-9 merger); else flag &gt; notify &gt; auto_approve_standalone. One immutable PolicyTranscript per fire, chained via SHA-256 RFC 8785 JCS.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Components */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wider">
              Core Components
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Confidence Scoring */}
              <div className="bg-surface-1 border border-border p-4">
                <h4 className="text-xs font-semibold text-foreground mb-2">Confidence Scoring System</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Every decision gets a 3-pillar triangulated confidence score (0.0–1.0). The engine suggests a status — your policies decide what action to take.
                </p>
                <div className="p-2 bg-surface-2 border border-border text-[10px] text-mono">
                  <div className="text-confidence">✓ score ≥ 0.7 → suggestedStatus: success</div>
                  <div className="text-review">⚠ 0.4 ≤ score &lt; 0.7 → suggestedStatus: flagged</div>
                  <div className="text-enforcement">✗ score &lt; 0.4 → suggestedStatus: escalated</div>
                </div>
              </div>

              {/* Policy Engine */}
              <div className="bg-surface-1 border border-border p-4">
                <h4 className="text-xs font-semibold text-foreground mb-2">Policy Engine — ADL v0.1 + Cedar substrate</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Author in ADL (typed predicate registry). Compiler lowers to AWS Cedar (CNCF Sandbox, formal semantics). Every change creates an immutable, SHA-256-chained <span className="text-mono">PolicyVersion</span>.
                </p>
                <div className="p-2 bg-surface-2 border border-border text-[10px] text-mono">
                  <div>when agent_equals("loan_underwriter")</div>
                  <div className="ml-2">and confidence_below(0.7)</div>
                  <div>then flag_for_review</div>
                </div>
              </div>

              {/* HITL */}
              <div className="bg-surface-1 border border-border p-4">
                <h4 className="text-xs font-semibold text-foreground mb-2">Human-in-the-Loop (HITL)</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Uncertain decisions go to a review queue where your team can approve/reject with full context.
                </p>
                <div className="p-2 bg-surface-2 border border-border text-[10px]">
                  <div className="text-muted-foreground">• Real-time queue with priority levels</div>
                  <div className="text-muted-foreground">• Full payload & context visibility</div>
                  <div className="text-muted-foreground">• SLA tracking (avg 7m response time)</div>
                </div>
              </div>

              {/* CPI */}
              <div className="bg-surface-1 border border-border p-4">
                <h4 className="text-xs font-semibold text-foreground mb-2">Compliance Performance Index (CPI)</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Real-time compliance score based on 6 EU AI Act dimensions. Track your readiness for audits.
                </p>
                <div className="p-2 bg-surface-2 border border-border text-[10px]">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-muted-foreground">Current Score:</span>
                    <span className="text-2xl font-bold text-mono text-foreground">96/100</span>
                  </div>
                  <div className="text-muted-foreground">Audit-ready status</div>
                </div>
              </div>

              {/* PolicyTranscript / Hash Chain — Phase 1 Track D */}
              <div className="bg-surface-1 border border-border p-4">
                <h4 className="text-xs font-semibold text-foreground mb-2">Court-admissible PolicyTranscript</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Every fired policy writes one append-only <span className="text-mono">PolicyTranscript</span> — SHA-256 chained (RFC 8785 JCS), pinned to the immutable PolicyVersion, signed by D-Trust QTSP at the daily Merkle anchor.
                </p>
                <div className="p-2 bg-surface-2 border border-border text-[10px] text-mono">
                  <div className="text-muted-foreground">policyVersionHash: a3b9…  (immutable)</div>
                  <div className="text-muted-foreground">cedarRequestHash: 7f2e…  (replayable)</div>
                  <div className="text-muted-foreground">algoSuite: sha256-jcs-rfc8785</div>
                </div>
              </div>

              {/* 3-Role Approval — Phase 2 Track B */}
              <div className="bg-surface-1 border border-border p-4">
                <h4 className="text-xs font-semibold text-foreground mb-2">3-role approval (BaFin MaRisk AT 4.3.1)</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Author + Reviewer + Enforcer must be distinct natural persons. Break-glass requires a typed-confirm phrase + 50-char justification, all recorded on the audit chain.
                </p>
                <div className="p-2 bg-surface-2 border border-border text-[10px] text-mono">
                  <div className="text-muted-foreground">submittedBy ≠ reviewedBy ≠ enforcedBy</div>
                  <div className="text-enforcement">AUTHOR_REVIEWER_SAME_PERSON → 403</div>
                  <div className="text-enforcement">AUTHOR_ENFORCER_SAME_PERSON → 403</div>
                </div>
              </div>
            </div>
          </div>

          {/* Integration Example */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wider">
              Integration Example (Node.js)
            </h3>
            
            <div className="p-4 bg-surface-2 border border-border overflow-x-auto">
              <pre className="text-[11px] text-mono text-muted-foreground">
{`import { Adjudon } from '@adjudon/node';

const adjudon = new Adjudon({
  apiKey: process.env.ADJUDON_API_KEY,
  agentId: 'payment-processor',
});

// Before executing any AI agent action:
async function processTransaction(amount, recipient) {
  // 1. Ingest the trace — the Policy Engine evaluates it inline
  const trace = await adjudon.trace({
    outputDecision: 'process_transaction',
    confidenceScore: 0.82,
    inputContext: JSON.stringify({ amount, recipient }),
  });

  // 2. trace.status reflects the action ladder (block wins; flag + auto_approve → auto-approve; else flag > notify > auto_approve_standalone)
  if (trace.status === 'blocked') {
    // HTTP 403 ADJ_BLOCKED_BY_POLICY — policy blocked the action
    throw new Error(trace.reason);
  }
  if (trace.status === 'flagged') {
    // HTTP 202 — held for human review; trace.reviewId is the queue entry
    return { status: 'pending', reviewId: trace.reviewId };
  }
  // HTTP 201 — proceed (covers approved / auto_approved / notify-only / no-match)
  return executePayment(amount, recipient);
}`}
              </pre>
            </div>
          </div>

          {/* Benefits */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wider">
              Why ADJUDON?
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="p-4 border border-border">
                <Shield className="w-5 h-5 text-muted-foreground mb-2" />
                <h4 className="text-xs font-semibold text-foreground mb-2">EU AI Act Art. 13 + 14, anchored</h4>
                <p className="text-xs text-muted-foreground">
                  Per-evaluation PolicyTranscript chained to D-Trust QTSP. 3-role approval matches BaFin MaRisk AT 4.3.1 functional separation.
                </p>
              </div>

              <div className="p-4 border border-border">
                <Zap className="w-5 h-5 text-muted-foreground mb-2" />
                <h4 className="text-xs font-semibold text-foreground mb-2">p95 &lt; 25ms hot path</h4>
                <p className="text-xs text-muted-foreground">
                  Confidence scoring + policy evaluation + audit-log write + webhook dispatch — fail-open: SDK returns pass-through if Adjudon times out.
                </p>
              </div>

              <div className="p-4 border border-border">
                <BarChart3 className="w-5 h-5 text-muted-foreground mb-2" />
                <h4 className="text-xs font-semibold text-foreground mb-2">Per-policy precision · recall · F1</h4>
                <p className="text-xs text-muted-foreground">
                  Effectiveness loop closes via reviewer verdicts (Cardinal Rule #6 — your own data only). Per-condition Shapley shows which rules earn their place.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Navigation */}
          <div className="p-4 border border-border bg-surface-1">
            <div className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground mb-3">Quick Navigation</div>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/dashboard/systems/new"
                onClick={onClose}
                className="adp-btn px-3 py-1.5 text-[10px] text-mono uppercase tracking-wider"
              >
                SDK Setup
              </Link>
              <Link
                to="/dashboard/policies"
                onClick={onClose}
                className="adp-btn px-3 py-1.5 text-[10px] text-mono uppercase tracking-wider"
              >
                Policy Engine
              </Link>
              <Link
                to="/dashboard/policies/_new/adl"
                onClick={onClose}
                className="adp-btn px-3 py-1.5 text-[10px] text-mono uppercase tracking-wider"
              >
                ADL Editor
              </Link>
              <Link
                to="/dashboard/policies/approvals"
                onClick={onClose}
                className="adp-btn px-3 py-1.5 text-[10px] text-mono uppercase tracking-wider"
              >
                Approval Queue
              </Link>
              <Link
                to="/dashboard/policies/effectiveness"
                onClick={onClose}
                className="adp-btn px-3 py-1.5 text-[10px] text-mono uppercase tracking-wider"
              >
                Effectiveness
              </Link>
              <Link
                to="/dashboard/policies/export"
                onClick={onClose}
                className="adp-btn px-3 py-1.5 text-[10px] text-mono uppercase tracking-wider"
              >
                Export Bundle
              </Link>
              <Link
                to="/dashboard/audit/policy-engine"
                onClick={onClose}
                className="adp-btn px-3 py-1.5 text-[10px] text-mono uppercase tracking-wider"
              >
                Auditor View
              </Link>
              <Link
                to="/dashboard/traces"
                onClick={onClose}
                className="adp-btn px-3 py-1.5 text-[10px] text-mono uppercase tracking-wider"
              >
                Decision Log
              </Link>
              <Link
                to="/dashboard/review-queue"
                onClick={onClose}
                className="adp-btn px-3 py-1.5 text-[10px] text-mono uppercase tracking-wider"
              >
                Review Queue
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}